import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { installChunkReloadHandler } from './lib/chunkReload';
import { storeLaunchFile, LAUNCH_RESTORE_EVENT } from './lib/launchRestore';
// Initialize i18next before anything renders so the first paint is already in
// the user's saved locale (and <html dir>/lang are set for RTL).
import './i18n/config';
import './styles/themes.css';
import './styles/optimize.css';
import './styles/desktop.css';
import './styles/body-desktop.css';
import './styles/train-desktop.css';
import './styles/meds-desktop.css';
import './styles/plan-desktop.css';
import './styles/food-desktop.css';
import './styles/settings-desktop.css';
import './styles/placeholder.css';
import './styles/today.css';
import './styles/settings.css';
import './styles/welcome.css';
import './styles/welcome-layout.css';
import './styles/body.css';
import './styles/train.css';
import './styles/train-live.css';
import './styles/food.css';
import './styles/meds.css';
import './styles/meds-calculator.css';
import './styles/plan.css';
import './styles/plan-builder.css';
import './styles/onboarding.css';
import './styles/admin.css';
import './components/lumen/lumen.css';
import './components/domain/avatar.css';
import './components/domain/macro-row.css';
import './components/domain/user-badge.css';
import './components/domain/wellbeing-sheet.css';
import './components/domain/mini-macro.css';
import './components/domain/adherence-tile.css';
import './styles/command-palette.css';
import './styles/rtl.css';

// File Handler launch queue: fires when the OS opens a .json file in the PWA.
// Stores the content in sessionStorage so RestoreBackup can pick it up once
// mounted, then navigates to /settings (or signals it if already there).
if ('launchQueue' in window) {
  (window as Window & { launchQueue: { setConsumer(fn: (p: { files: FileSystemFileHandle[] }) => void): void } }).launchQueue.setConsumer(
    async (params) => {
      const [handle] = params.files ?? [];
      if (!handle) return;
      const file = await handle.getFile();
      if (!file.name.endsWith('.json')) return;
      const text = await file.text();
      try { JSON.parse(text); } catch { return; }
      storeLaunchFile(text);
      if (window.location.pathname === '/settings') {
        window.dispatchEvent(new CustomEvent(LAUNCH_RESTORE_EVENT));
      } else {
        window.location.href = '/settings';
      }
    },
  );
}

// Recover from stale code-split chunks after a deploy (see lib/chunkReload).
// Catches dynamic imports / preloaded CSS that 404 because a fresh build rotated
// the content-hashed filenames out from under a cached index.html.
installChunkReloadHandler();

const root = document.getElementById('root');
if (!root) throw new Error('#root not found');
createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>
);
